import { useState } from 'react';
import { T, Var, useGT } from 'gt-react';
import {
  DotsThreeVerticalIcon,
  PencilSimpleIcon,
  TrashIcon,
  WarningIcon,
} from '@phosphor-icons/react';
import type { AgentSopSummary } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { Input } from 'src/components/ui/input';
import { Label } from 'src/components/ui/label';
import { AlertContainer, AlertDescription } from 'src/components/ui/alert';
import { useToast } from 'src/context/ToastContext';
import {
  useDeleteSop,
  useRenameSop,
  useSops,
} from 'src/api/rpc/hooks/agent/useOperatorSops';
import { slugify } from '../interviewIntake';

type ProcedureIdentity = Pick<
  AgentSopSummary,
  'id' | 'title' | 'name' | 'builtinKey'
>;

function procedureLabel(sop: ProcedureIdentity): string {
  return sop.title.trim() || sop.name;
}

/** Names of the other procedures whose title reads the same (trimmed, case-insensitive). */
function sameTitleNames(
  sops: readonly ProcedureIdentity[],
  selfId: string,
  title: string,
): string[] {
  const key = title.trim().toLocaleLowerCase();
  if (!key) return [];
  return sops
    .filter(
      (other) =>
        other.id !== selfId && other.title.trim().toLocaleLowerCase() === key,
    )
    .map((other) => other.name);
}

function RenameProcedureDialog({
  organizationId,
  sop,
  open,
  onOpenChange,
}: {
  organizationId: string;
  sop: ProcedureIdentity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const [title, setTitle] = useState(sop.title);
  const [error, setError] = useState<string | null>(null);
  const rename = useRenameSop();
  const handle = sop.builtinKey ? sop.name : slugify(title);
  // Titles may repeat, but agents resolve a procedure by its title, so flag a clash before save.
  // The server re-checks the whole org on save; this list covers the first page only.
  const { sops } = useSops({ organizationId, limit: 100 });
  const titleClashes = sameTitleNames(sops, sop.id, title);

  function submit() {
    setError(null);
    rename.mutate(
      { organizationId, sopId: sop.id, title },
      {
        onSuccess: (result) => {
          onOpenChange(false);
          if (result.titleSharedWith.length > 0) {
            showToast({
              color: 'warning',
              title: gt('Procedure renamed', {
                context: 'toast title after renaming a procedure',
              }),
              message: gt(
                'Another procedure has the same title ({names}). Agents look procedures up by title, so they may pick the wrong one.',
                {
                  names: result.titleSharedWith
                    .map((other) => other.name)
                    .join(', '),
                  context:
                    'warning toast after renaming a procedure to a title another procedure already uses',
                },
              ),
            });
            return;
          }
          showToast({
            color: 'success',
            title: gt('Procedure renamed', {
              context: 'toast title after renaming a procedure',
            }),
          });
        },
        onError: (renameError: Error) => {
          setError(
            renameError.message ||
              gt('Could not rename this procedure.', {
                context: 'error renaming a procedure',
              }),
          );
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title to rename a procedure">Rename procedure</T>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor={`rename-${sop.id}`}>
            <T context="label for the procedure name field">Name</T>
          </Label>
          <Input
            id={`rename-${sop.id}`}
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setError(null);
            }}
            autoFocus
          />
          <p className="text-xs text-muted-foreground">
            {sop.builtinKey ? (
              <T context="note that a built-in procedure keeps its system handle">
                Built-in procedures keep their system handle.
              </T>
            ) : (
              <T context="preview of the procedure handle derived from the new name">
                Handle: <Var>{handle || '—'}</Var>
              </T>
            )}
          </p>
          {titleClashes.length > 0 && (
            <AlertContainer>
              <WarningIcon />
              <AlertDescription>
                <T context="warning that a procedure with the same title already exists">
                  Another procedure already uses this title (
                  <span className="font-mono text-xs">
                    <Var>{titleClashes.join(', ')}</Var>
                  </span>
                  ). Agents look procedures up by title, so they may pick the
                  wrong one.
                </T>
              </AlertDescription>
            </AlertContainer>
          )}
          {error && (
            <AlertContainer variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </AlertContainer>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <T context="cancel renaming a procedure">Cancel</T>
          </Button>
          <Button
            onClick={submit}
            disabled={rename.isPending || title.trim().length === 0}
          >
            <T context="save a renamed procedure">Save</T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteProcedureDialog({
  organizationId,
  sop,
  open,
  onOpenChange,
  onDeleted,
}: {
  organizationId: string;
  sop: ProcedureIdentity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted?: () => void;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const [error, setError] = useState<string | null>(null);
  const remove = useDeleteSop();
  const label = procedureLabel(sop);

  function submit() {
    setError(null);
    remove.mutate(
      { organizationId, sopId: sop.id },
      {
        onSuccess: () => {
          onOpenChange(false);
          showToast({
            color: 'success',
            title: gt('Procedure deleted', {
              context: 'toast title after soft-deleting a procedure',
            }),
          });
          onDeleted?.();
        },
        onError: (deleteError: Error) => {
          setError(
            deleteError.message ||
              gt('Could not delete this procedure.', {
                context: 'error deleting a procedure',
              }),
          );
        },
      },
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title to delete a procedure">Delete procedure</T>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm">
            <T context="confirm soft-deleting a procedure that is not in use">
              Delete “<Var>{label}</Var>”? It leaves the library. History of
              runs that already used it is kept.
            </T>
          </p>
          {error && (
            <AlertContainer variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </AlertContainer>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <T context="cancel deleting a procedure">Cancel</T>
          </Button>
          <Button
            variant="destructive"
            onClick={submit}
            disabled={remove.isPending}
          >
            <T context="confirm delete procedure button">Delete</T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Rename and soft-delete for one procedure. Delete is refused while something
 * still uses it; the server names those uses and the dialog shows that list.
 */
export function ProcedureRowActions({
  organizationId,
  sop,
  onDeleted,
}: {
  organizationId: string;
  sop: ProcedureIdentity;
  onDeleted?: () => void;
}) {
  const gt = useGT();
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  return (
    <div onClick={(event) => event.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={gt('Procedure actions', {
              context: 'aria label for the procedure row actions menu',
            })}
          >
            <DotsThreeVerticalIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => setRenameOpen(true)}>
            <PencilSimpleIcon className="mr-2 h-4 w-4" />
            <T context="menu item to rename a procedure">Rename</T>
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            onClick={() => setDeleteOpen(true)}
          >
            <TrashIcon className="mr-2 h-4 w-4" />
            <T context="menu item to delete a procedure">Delete</T>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {renameOpen && (
        <RenameProcedureDialog
          organizationId={organizationId}
          sop={sop}
          open={renameOpen}
          onOpenChange={setRenameOpen}
        />
      )}
      {deleteOpen && (
        <DeleteProcedureDialog
          organizationId={organizationId}
          sop={sop}
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          onDeleted={onDeleted}
        />
      )}
    </div>
  );
}
